import { nanoid } from 'nanoid';
import { getMultilingualText } from './multilingualHelper';

/**
 * Creates a new Key Individual/Entity section.
 * @param {Object} templateSection - The original (template) section object.
 * @param {number} counter - The number for the newly created section (e.g., 2, 3...).
 * @param {number} clientFormOriginalFieldCount - Number of original fields to include.
 * @param {string} currentLanguage - Current language for label processing.
 * @returns {Object} - The newly created section object with matched IDs.
 */
export const generateKeyIndividualOrEntitySection = (templateSection, counter, clientFormOriginalFieldCount, currentLanguage = 'en') => {
    const newIdMap = {};
    const uboId = `ubo_${counter}`;
    const newLabelPrefix = `${counter}`;

    const newFields = templateSection.fields.slice(0, clientFormOriginalFieldCount).map(field => {
        const newId = nanoid();
        newIdMap[field.id] = newId;

        const originalLabel = getMultilingualText(field.label, currentLanguage);
        const newLabel = `${originalLabel} ${newLabelPrefix}`;

        // Create new field without value to ensure clean state
        const { value, ...fieldWithoutValue } = field;

        return {
            ...fieldWithoutValue,
            id: newId,
            label: newLabel,
            config: {
                ...field.config,
                fieldName: field.config.fieldName || '',
                uboId: uboId
            }
        };
    });

    const newRules = (templateSection.config.rules || []).map(rule => {
        const updatedConditions = rule.conditions.map(cond => ({
            ...cond,
            field: {
                ...cond.field,
                id: newIdMap[cond.field.id] || nanoid(),
                config: {
                    ...cond.field.config
                }
            }
        }));

        const updatedActions = rule.actions.map(action => ({
            ...action,
            target: {
                ...action.target,
                id: newIdMap[action.target.id] || nanoid(),
                config: {
                    ...action.target.config
                }
            }
        }));

        return {
            ...rule,
            ruleName: rule.ruleName ? `${rule.ruleName}_${counter}` : `Rule ${counter}`,
            conditions: updatedConditions,
            actions: updatedActions
        };
    });

    return {
        ...templateSection,
        id: nanoid(),
        fields: newFields,
        config: {
            ...templateSection.config,
            rules: newRules
        }
    };
};